import { usePortfolioBiSummary } from '@/api/hooks/useVendors';
import { BandHeader, BandCards, HomeCard, BigStat, StatPair, MixBar, CardHint, DrillLink, BandError } from './HomeBandKit';

// Home Page BI — Vendor band (entitlement-gated 'vrm'). One summary call (/vendors/bi-summary).
// Findings drill to the VENDOR module (value and destination must agree).
const TIER_META: Array<[string, string, string]> = [
  ['tier_1_critical', 'Tier 1', '#C2453F'],
  ['tier_2_high', 'Tier 2', '#D7A94A'],
  ['tier_3_medium', 'Tier 3', '#3E7FA8'],
  ['tier_4_low', 'Tier 4', '#4A9E78'],
];

function LoadingCard() {
  return <HomeCard cap="Loading"><CardHint>Loading…</CardHint></HomeCard>;
}

export function VendorHomeBand() {
  const { data, isLoading, isError, refetch } = usePortfolioBiSummary('90d');

  const header = (
    <BandHeader tab="#4FA8E8" title="Vendors" chip="Vendor" chipTone="vrm" drillLabel="Vendor Workspace →" drillPath="/vendors" />
  );

  if (isError) {
    return (
      <section className="flex flex-col gap-[9px]" data-testid="home-band-vendor" aria-label="Vendors">
        {header}
        <BandError title="Vendor summary" onRetry={() => refetch()} />
      </section>
    );
  }

  const kpis = data?.kpis;
  const byTier = data?.distributions.by_tier ?? {};
  const tiers = TIER_META.map(([k, label, tone]) => ({ label, tone, count: byTier[k] ?? 0 })).filter((t) => t.count > 0);

  return (
    <section className="flex flex-col gap-[9px]" data-testid="home-band-vendor" aria-label="Vendors">
      {header}
      {isLoading ? (
        <BandCards><LoadingCard /></BandCards>
      ) : (
        <BandCards>
          <HomeCard cap="Portfolio">
            <BigStat value={kpis?.active_vendors ?? 0} unit="active vendors" />
            <MixBar segments={tiers} />
            <CardHint>Tier mix as a share of the portfolio · critical tiers first</CardHint>
            <DrillLink label="Open the vendor list →" path="/vendors" />
          </HomeCard>

          <HomeCard cap="Assessments">
            <StatPair
              left={{ n: kpis?.assessments_in_flight ?? 0, l: 'in flight' }}
              right={{ n: kpis?.assessments_overdue ?? 0, l: 'overdue', color: '#F09E99' }}
            />
            <CardHint>Full assessment pipeline on the Assessments workspace.</CardHint>
            <DrillLink label="View overdue assessments →" path="/assessments?overdue=true" />
          </HomeCard>

          <HomeCard cap="Open findings & certifications">
            <StatPair
              left={{ n: kpis?.open_findings ?? 0, l: 'open vendor findings', color: '#F0C674' }}
              right={{ n: kpis?.certs_expiring_90d ?? 0, l: 'certs expiring in 90d', color: '#F0C674' }}
            />
            <CardHint>Vendor findings only.</CardHint>
            <DrillLink label="View vendor findings →" path="/vendors" />
          </HomeCard>
        </BandCards>
      )}
    </section>
  );
}
